import type { ConnectionType } from "@workos-inc/node";

export const PROVIDER_LOGIN_COMPLETE_MESSAGE = "offstreet:provider-login-complete";

export type ProviderLoginPopup = { window: Window; popupId: string };

export type ProviderLoginOutcome = "completed" | "failed" | "dismissed";

const CLOSED_POLL_MS = 500;
const CLOSE_GRACE_MS = 1_000;
const ABANDONED_TIMEOUT_MS = 5 * 60_000;

export const openProviderLoginPopup = ({
	provider,
}: { provider: `${ConnectionType}` | undefined }): ProviderLoginPopup | null => {
	const popupId = crypto.randomUUID();
	// OAuth consent screens are built for a popup; SAML and OIDC identity providers show a sign-in page built for a
	// full browser tab, often with a 620px-wide Duo prompt.
	const size = provider?.endsWith("OAuth") ? "width=500,height=700" : "width=800,height=720";
	const popup = window.open("", `offstreet-provider-login-${popupId}`, `popup=yes,${size}`);
	return popup ? { window: popup, popupId } : null;
};

export const waitForProviderLoginPopup = ({ popup, signal }: { popup: ProviderLoginPopup; signal: AbortSignal }) =>
	new Promise<ProviderLoginOutcome>((resolve) => {
		if (signal.aborted) {
			resolve("dismissed");
			return;
		}
		const listeners = new AbortController();
		const timers: { closedPoll?: number; closeGrace?: number; abandoned?: number } = {};

		const settle = (outcome: ProviderLoginOutcome) => {
			listeners.abort();
			window.clearInterval(timers.closedPoll);
			window.clearTimeout(timers.closeGrace);
			window.clearTimeout(timers.abandoned);
			resolve(outcome);
		};

		window.addEventListener(
			"message",
			(event: MessageEvent) => {
				if (event.source !== popup.window || event.origin !== window.location.origin) {
					return;
				}
				if (event.data?.type !== PROVIDER_LOGIN_COMPLETE_MESSAGE || event.data?.popupId !== popup.popupId) {
					return;
				}
				settle(event.data.failed ? "failed" : "completed");
			},
			{ signal: listeners.signal },
		);

		signal.addEventListener("abort", () => settle("dismissed"), { signal: listeners.signal });

		timers.closedPoll = window.setInterval(() => {
			if (!popup.window.closed) {
				return;
			}
			window.clearInterval(timers.closedPoll);
			timers.closeGrace = window.setTimeout(() => settle("dismissed"), CLOSE_GRACE_MS);
		}, CLOSED_POLL_MS);

		timers.abandoned = window.setTimeout(() => settle("dismissed"), ABANDONED_TIMEOUT_MS);
	});
